class CartDrawer extends HTMLElement {
    constructor() {
      super();
  
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.setHeaderCartIconAccessibility();
    }
  
    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;
  
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  
    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });
  
      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );
  
      document.body.classList.add('overflow-hidden');
    }
  
    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }
  
    setSummaryAccessibility(cartDrawerNote) {
      if (!cartDrawerNote) return;
      
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      if (cartDrawerNote.parentElement) {
        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
      }
    }
  
    renderContents(parsedState) {
      this.productId = parsedState.id;
      
      // Check if cart drawer element is empty or missing content
      const cartDrawerElement = this.querySelector('#CartDrawer');
      const hasCartDrawerContent = cartDrawerElement && this.innerHTML.trim() !== '';
      
      // Check if cart-drawer section is missing (happens when product URL contains "cart")
      const cartDrawerSection = parsedState.sections && parsedState.sections['cart-drawer'];
      
      // If section is missing OR cart drawer is empty, fetch it directly
      if (!cartDrawerSection || !hasCartDrawerContent) {
        // Fetch cart drawer section directly as fallback
        return this.fetchCartDrawerSection(parsedState);
      }
      
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner && drawerInner.classList.contains('is-empty')) {
        drawerInner.classList.remove('is-empty');
      }
      
      this.getSectionsToRender().forEach((section) => {
        const sectionHTML = parsedState.sections && parsedState.sections[section.id];
        if (!sectionHTML) return;
        
        let sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        // If cart drawer element is empty, inject the full section HTML
        if (!sectionElement && section.id === 'cart-drawer') {
          const fullSectionHTML = this.getSectionInnerHTML(sectionHTML, '.shopify-section');
          if (fullSectionHTML) {
            this.innerHTML = fullSectionHTML;
            sectionElement = this.querySelector(section.selector);
          }
        }

        if (!sectionElement) return;
        
        const newInnerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
        if (newInnerHTML) {
          sectionElement.innerHTML = newInnerHTML;
        }
      });

      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', this.close.bind(this));
        }
        this.open();
      });
    }
    
    fetchCartDrawerSection(parsedState) {
      // Fetch cart drawer section directly from Shopify
      // Use cart URL to avoid issues with product URLs containing "cart"
      const fetchUrl = `${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`;
      
      fetch(fetchUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          // Shopify returns sections wrapped in .shopify-section, then cart-drawer inside
          const shopifySection = html.querySelector('.shopify-section');
          let cartDrawerElement = shopifySection ? shopifySection.querySelector('cart-drawer') : html.querySelector('cart-drawer');
          
          // If not found, try finding by ID or class
          if (!cartDrawerElement) {
            cartDrawerElement = html.querySelector('#CartDrawer')?.closest('cart-drawer') || 
                               html.querySelector('.drawer')?.closest('cart-drawer') ||
                               html.body.querySelector('cart-drawer');
          }
          
          if (!cartDrawerElement) {
            console.error('Cart drawer element not found in fetched HTML. Response:', responseText.substring(0, 500));
            return;
          }
          
          // Always inject the full structure if cart drawer is empty
          if (this.innerHTML.trim() === '' || !this.querySelector('#CartDrawer')) {
            this.innerHTML = cartDrawerElement.innerHTML;
          } else {
            // Update existing content - find #CartDrawer and update it
            const cartDrawerSection = cartDrawerElement.querySelector('#CartDrawer');
            if (cartDrawerSection) {
              const existingCartDrawer = this.querySelector('#CartDrawer');
              if (existingCartDrawer) {
                existingCartDrawer.innerHTML = cartDrawerSection.innerHTML;
              } else {
                // If #CartDrawer doesn't exist, inject the full structure
                this.innerHTML = cartDrawerElement.innerHTML;
              }
            } else {
              // If #CartDrawer not found in fetched content, inject everything
              this.innerHTML = cartDrawerElement.innerHTML;
            }
          }
          
          // Update cart-icon-bubble if available from parsedState
          if (parsedState && parsedState.sections && parsedState.sections['cart-icon-bubble']) {
            const cartIconBubble = document.getElementById('cart-icon-bubble');
            if (cartIconBubble) {
              const iconHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble'], '.shopify-section');
              if (iconHTML) {
                cartIconBubble.innerHTML = iconHTML;
              }
            }
          }
          
          // Update cart drawer with current cart data
          // Use onCartUpdate to refresh the cart contents with current items
          const cartDrawerItems = this.querySelector('cart-drawer-items');
          if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
            // This will fetch and update cart-drawer-items and cart-drawer__footer with current cart
            cartDrawerItems.onCartUpdate().then(() => {
              this.finalizeCartDrawerOpen(parsedState);
            }).catch(() => {
              // If onCartUpdate fails, still try to open
              this.finalizeCartDrawerOpen(parsedState);
            });
          } else {
            // If onCartUpdate not available, open immediately
            this.finalizeCartDrawerOpen(parsedState);
          }
        })
        .catch((e) => {
          console.error('Failed to fetch cart drawer section:', e);
        });
    }
    
    finalizeCartDrawerOpen(parsedState) {
      // Update drawer state
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        if (drawerInner.classList.contains('is-empty')) {
          drawerInner.classList.remove('is-empty');
        }
        // Update is-empty class on cart-drawer element based on item count
        if (parsedState && parsedState.item_count === 0) {
          this.classList.add('is-empty');
        } else {
          this.classList.remove('is-empty');
        }
      } else {
        // If drawer__inner doesn't exist, the content injection might have failed
        // Try to fetch one more time as a last resort
        console.warn('Cart drawer inner not found, content may not have been injected properly');
      }
      
      // Ensure overlay event listener is attached and open the drawer
      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', this.close.bind(this));
        }
        
        // Verify we have content before opening
        const cartDrawerContent = this.querySelector('#CartDrawer');
        if (cartDrawerContent || this.innerHTML.trim() !== '') {
          this.open();
        } else {
          console.error('Cart drawer content is still empty, cannot open drawer');
        }
      }, 100);
    }
  
    getSectionInnerHTML(html, selector = '.shopify-section') {
      if (!html) return '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const element = doc.querySelector(selector);
      if (element) return element.innerHTML;
      
      // Fallback: try to find in .shopify-section if selector not found directly
      const shopifySection = doc.querySelector('.shopify-section');
      if (shopifySection) {
        const nestedElement = shopifySection.querySelector(selector);
        if (nestedElement) return nestedElement.innerHTML;
        // If still not found, return the entire shopify-section content
        return shopifySection.innerHTML;
      }
      
      // Last resort: return body content
      return doc.body ? doc.body.innerHTML : '';
    }
  
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }
  
    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }
  
    setActiveElement(element) {
      this.activeElement = element;
    }
  }
  
  customElements.define('cart-drawer', CartDrawer);
  
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }
  
  customElements.define('cart-drawer-items', CartDrawerItems);
  